import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Modal from '../../components/Modal';
import ProductCategoryService from '../../components/ProductCategoryService';
import PlateformService from '../../components/PlateformService';

const emptyCategory = {
  name: '',
  description: '',
  type: 'pegasus',
  enable: true,
  parent_id: '',
  remote_plateform_id: '',
  link_ids: [],
  image: '',
  // internal flag: whether user modified image (used to decide whether to include image field in update)
  _imageTouched: false,
};

const extractId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (value._id) return value._id;
    if (value.id) return value.id;
    if (typeof value.toString === 'function') return value.toString();
  }
  return '';
};

function ProductCategoriesView() {
  const resolveImageUrl = (img) => {
    if (!img) return null;
    if (/^https?:\/\//i.test(img)) return img;
    const base = process.env.REACT_APP_PEGASUS_API_URL || '';
    return `${base}${img}`;
  };

  const [categories, setCategories] = useState([]);
  const [plateforms, setPlateforms] = useState([]);
  const [formData, setFormData] = useState(emptyCategory);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async () => {
    const data = await ProductCategoryService.fetchCategories();
    setCategories(data.response || data);
  };

  const fetchPlateforms = async () => {
    const data = await PlateformService.fetchPlateforms();
    setPlateforms(data.response || data);
  };

  useEffect(() => {
    fetchData();
    fetchPlateforms();
  }, []);

  useEffect(() => {
    if (page * rowsPerPage >= categories.length && page !== 0) {
      setPage(0);
    }
  }, [categories.length, page, rowsPerPage]);

  const sanitizePayload = useMemo(
    () => (data) => {
      const payload = {
        name: data.name.trim(),
        description: data.description.trim(),
        type: data.type.trim() || undefined,
        enable: !!data.enable,
        parent_id: data.parent_id || null,
        remote_plateform_id: data.remote_plateform_id.trim(),
        link_ids: Array.isArray(data.link_ids) ? data.link_ids.filter(Boolean) : [],
      };

      if (!payload.description) delete payload.description;
      if (!payload.type) delete payload.type;
      if (!payload.remote_plateform_id) delete payload.remote_plateform_id;
      if (!payload.parent_id) payload.parent_id = null;

      // Image handling:
      // - For create: include image if provided (base64 string)
      // - For update: include image only when user touched the image control. If touched and image is null -> remove image; if touched and image is string -> set/replace image.
      if (data._imageTouched) {
        // allow explicit null to remove image
        if (data.image === null || data.image === 'null') payload.image = null;
        else if (typeof data.image === 'string' && data.image.trim() !== '') payload.image = data.image;
        else payload.image = null;
      }

      return payload;
    },
    []
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'link_ids') {
      const normalizedValue = typeof value === 'string' ? value.split(',') : value;
      setFormData((prev) => ({ ...prev, link_ids: normalizedValue }));
      return;
    }

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      setFormData((prev) => ({ ...prev, image: base64, _imageTouched: true }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null, _imageTouched: true }));
  };

  const handleSubmit = async () => {
    const payload = sanitizePayload(formData);
    if (editingId) await ProductCategoryService.updateCategory(editingId, payload);
    else await ProductCategoryService.createCategory(payload);
    setFormData({ ...emptyCategory });
    setEditingId(null);
    fetchData();
  };

  const handleEdit = (category) => {
    setEditingId(category._id || category.id);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      type: category.type || 'pegasus',
      enable: typeof category.enable === 'boolean' ? category.enable : true,
      parent_id: extractId(category.parent_id),
      remote_plateform_id: extractId(category.remote_plateform_id),
      link_ids: Array.isArray(category.link_ids)
        ? category.link_ids.map((link) => extractId(link)).filter(Boolean)
        : [],
      image: category.image ? resolveImageUrl(category.image) : '',
      _imageTouched: false,
    });
  };

  const handleDelete = async (id) => {
    await ProductCategoryService.deleteCategory(id);
    fetchData();
  };

  const renderCategoryName = (id) => {
    const normalizedId = extractId(id);
    const category = categories.find((c) => extractId(c._id || c.id) === normalizedId);
    return category ? category.name : '—';
  };

  const paginatedCategories = useMemo(() => {
    const start = page * rowsPerPage;
    return categories.slice(start, start + rowsPerPage);
  }, [categories, page, rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSubmitDisabled = !formData.name.trim();

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" mb={3} spacing={2}>
        <Typography variant="h4" component="h1">
          Catégories de produits
        </Typography>
        <Button
          variant="contained"
          color="primary"
          data-bs-toggle="modal"
          data-bs-target="#categoryModal"
          onClick={() => {
            setFormData({ ...emptyCategory });
            setEditingId(null);
          }}
        >
          Ajouter une catégorie
        </Button>
      </Stack>
      <Paper elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell>Catégorie parente</TableCell>
                <TableCell>Plateforme distante</TableCell>
                <TableCell>Catégories liées</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Aucune catégorie trouvée.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCategories.map((c) => {
                  const parentId = extractId(c.parent_id);
                  const linkIds = Array.isArray(c.link_ids)
                    ? c.link_ids.map((link) => extractId(link)).filter(Boolean)
                    : [];
                  const remotePlateformId = extractId(c.remote_plateform_id);
                  const remotePlateform = plateforms.find(
                    (p) => extractId(p._id || p.id) === remotePlateformId
                  );
                  const linkedNames = linkIds
                    .map((id) => renderCategoryName(id))
                    .filter(Boolean)
                    .join(', ');

                  return (
                    <TableRow key={c._id || c.id} hover>
                      <TableCell>
                        {c.image ? (
                          <img
                            src={resolveImageUrl(c.image)}
                            alt={c.name}
                            style={{ height: 48, width: 'auto', borderRadius: 6 }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{c.name}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" color="text.secondary">
                          {c.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{c.type || 'pegasus'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={c.enable === false ? 'Non' : 'Oui'}
                          color={c.enable === false ? 'default' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{parentId ? renderCategoryName(parentId) : '—'}</TableCell>
                      <TableCell>
                        {remotePlateform ? remotePlateform.name : remotePlateformId || '—'}
                      </TableCell>
                      <TableCell>{linkedNames || '—'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          size="small"
                          data-bs-toggle="modal"
                          data-bs-target="#categoryModal"
                          onClick={() => handleEdit(c)}
                          aria-label="Modifier la catégorie"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(c._id || c.id)}
                          aria-label="Supprimer la catégorie"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={categories.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      <Modal
        modalId="categoryModal"
        modalType="modal-xxl"
        onSubmit={handleSubmit}
        submitLabel={editingId ? 'Modifier' : 'Ajouter'}
        submitDisabled={isSubmitDisabled}
        title={editingId ? 'Modifier la catégorie' : 'Ajouter une catégorie'}
      >
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Nom"
            fullWidth
          />
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Description"
            fullWidth
            multiline
            minRows={3}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="type-label">Type</InputLabel>
              <Select
                labelId="type-label"
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
              >
                <MenuItem value="pegasus">pegasus</MenuItem>
                <MenuItem value="other">other</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel shrink>Image (optionnel)</InputLabel>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button variant="outlined" component="label">
                  Choisir une image
                  <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                </Button>
                <Button variant="text" color="error" onClick={handleRemoveImage}>
                  Supprimer
                </Button>
              </Box>
              {formData.image ? (
                <Box sx={{ mt: 1 }}>
                  <img src={formData.image} alt="preview" style={{ maxHeight: 120, borderRadius: 6 }} />
                </Box>
              ) : null}
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="parent-category-label">Catégorie parente</InputLabel>
              <Select
                labelId="parent-category-label"
                label="Catégorie parente"
                name="parent_id"
                value={formData.parent_id}
                onChange={handleChange}
              >
                <MenuItem value="">Aucune</MenuItem>
                {categories
                  .filter((cat) => (cat._id || cat.id) !== editingId)
                  .map((cat) => (
                    <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="remote-plateform-label">Plateforme distante</InputLabel>
              <Select
                labelId="remote-plateform-label"
                label="Plateforme distante"
                name="remote_plateform_id"
                value={formData.remote_plateform_id}
                onChange={handleChange}
              >
                <MenuItem value="">Aucune</MenuItem>
                {plateforms.map((p) => (
                  <MenuItem key={p._id || p.id} value={p._id || p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="linked-categories-label">Catégories liées</InputLabel>
              <Select
                labelId="linked-categories-label"
                label="Catégories liées"
                multiple
                name="link_ids"
                value={formData.link_ids}
                onChange={handleChange}
                input={<OutlinedInput label="Catégories liées" />}
                renderValue={(selected) =>
                  selected.length
                    ? selected
                        .map((id) => renderCategoryName(id))
                        .filter(Boolean)
                        .join(', ')
                    : 'Aucune'
                }
              >
                {categories
                  .filter((cat) => (cat._id || cat.id) !== editingId)
                  .map((cat) => (
                    <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
          <FormControlLabel
            control={<Switch name="enable" checked={formData.enable} onChange={handleChange} />}
            label="Catégorie active"
          />
        </Box>
      </Modal>
    </Box>
  );
}

export default ProductCategoriesView;
